import React, { useState } from 'react';
import PropTypes from 'prop-types';

const STATUS_META = {
  pass: {
    label: 'Pass',
    icon: '🟢',
  },
  warning: {
    label: 'Warning',
    icon: '🟡',
  },
  fail: {
    label: 'Needs attention',
    icon: '🔴',
  },
};

const ComplianceCheckCard = ({ check }) => {
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_META[check.status] || STATUS_META.fail;

  return (
    <article className={`compliance-card status-${check.status}`}>
      <header className="card-header">
        <div className="card-title-group">
          <span className="status-icon" aria-hidden="true">
            {status.icon}
          </span>
          <div className="card-heading">
            <h3>{check.title}</h3>
            <p className="card-summary">{check.summary}</p>
          </div>
        </div>
        <button
          type="button"
          className="details-toggle"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-controls={`${check.id}-details`}
        >
          {expanded ? 'Hide details' : 'View details'}
        </button>
      </header>

      {expanded && (
        <div className="card-body" id={`${check.id}-details`}>
          <p className="card-description">{check.description}</p>

          {check.status !== 'pass' && check.fixSteps?.length > 0 && (
            <div className="card-section">
              <h4>How to fix it</h4>
              <ul>
                {check.fixSteps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ul>
            </div>
          )}

          {check.insights?.length > 0 && (
            <div className="card-section">
              <h4>What we found</h4>
              <ul>
                {check.insights.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {check.cmpInfo && (
            <div className="card-section cmp-info">
              <h4>What is a CMP?</h4>
              <p className="cmp-description">{check.cmpInfo.whatIsCMP}</p>
              
              <h4 style={{ marginTop: '20px' }}>Why do you need it?</h4>
              <p className="cmp-description">{check.cmpInfo.whyYouNeedIt}</p>
              
              <h4 style={{ marginTop: '20px' }}>Recommended CMP Solutions</h4>
              <ul className="cmp-list">
                {check.cmpInfo.endorsedCMPs.map((cmp, index) => (
                  <li key={index} className={index === 0 ? 'cmp-featured' : ''}>
                    <a 
                      href={cmp.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="cmp-link"
                    >
                      <strong>{cmp.name}</strong>
                      {index === 0 && <span className="cmp-badge">Recommended</span>}
                    </a>
                    <p className="cmp-description-small">{cmp.description}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {check.resources?.length > 0 && (
            <div className="card-section resources">
              <h4>Recommended resources</h4>
              <ul>
                {check.resources.map((resource) => (
                  <li key={resource.href}>
                    <a href={resource.href} target="_blank" rel="noopener noreferrer">
                      {resource.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {check.action && (
            <div className="card-actions">
              {check.action.onClick ? (
                <button
                  onClick={check.action.onClick}
                  className="card-action"
                >
                  {check.action.label}
                </button>
              ) : (
                <a
                  href={check.action.href}
                  className="card-action"
                  target={check.action.external ? '_blank' : '_self'}
                  rel={check.action.external ? 'noopener noreferrer' : undefined}
                >
                  {check.action.label}
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
};

ComplianceCheckCard.propTypes = {
  check: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['pass', 'warning', 'fail']).isRequired,
    summary: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    fixSteps: PropTypes.arrayOf(PropTypes.string),
    insights: PropTypes.arrayOf(PropTypes.string),
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        href: PropTypes.string.isRequired,
      })
    ),
    action: PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      external: PropTypes.bool,
    }),
    cmpInfo: PropTypes.shape({
      whatIsCMP: PropTypes.string,
      whyYouNeedIt: PropTypes.string,
      endorsedCMPs: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          description: PropTypes.string.isRequired,
        })
      ),
    }),
  }).isRequired,
};

export default ComplianceCheckCard;



